import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { SubmitButton } from "@/components/ui/submit-button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { useTabErrorNavigation } from "@/hooks/useTabErrorNavigation";
import { cn, errorHandler } from "@/lib/utils";
import { userService } from "@/services/api/user";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Bell,
  Car,
  ChevronLeft,
  ChevronRight,
  Settings,
  User as UserIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  SubmitErrorHandler,
  useFieldArray,
  useForm,
  type Resolver,
} from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import UserDataTab from "./UserDataTab";
import UserFormSkeleton from "./UserFormSkeleton";
import UserModulesTab from "./UserModulesTab";
import UserNotificationsTab from "./UserNotificationsTab";
import UserVehiclesTab from "./UserVehiclesTab";

// Form validation schema
const formSchema = z.object({
  // User Data
  user_data: z.object({
    name: z.string().min(2, "Nome deve ter pelo menos 2 caracteres"),
    email: z.string().email("Email inválido").nullable(),
    user: z.string().nullable(),
    password: z
      .string()
      .min(8, "A senha deve ter pelo menos 8 caracteres")
      .regex(/[a-z]/, "A senha deve conter ao menos uma letra minúscula")
      .regex(/[A-Z]/, "A senha deve conter ao menos uma letra maiúscula")
      .regex(/[0-9]/, "A senha deve conter ao menos um número")
      .regex(/[^a-zA-Z0-9]/, "A senha deve conter ao menos um símbolo")
      .nullable(),
    status: z.number().min(0).max(1),
    enable_notifications: z.boolean().default(false),
    access_level_id: z.number().nullable().optional(),
  }),
  // Vehicles
  vehicles: z
    .array(
      z.object({
        vehicle_id: z.number(),
        permission: z.number(),
        title: z.string().nullable(),
        plate: z.string().nullable(),
      }),
    )
    .optional(),
  // Modules
  modules: z
    .array(
      z.object({
        module_id: z.number(),
        permission: z.number(),
        module_title: z.string(),
        parent_id: z.number().nullable(),
      }),
    )
    .optional(),
});

export type UserFormValues = z.infer<typeof formSchema>;

const STEPS = [
  {
    id: "user_data",
    label: "Dados do usuário",
    icon: UserIcon,
    priority: 1,
  },
  {
    id: "vehicles",
    label: "Veículos",
    icon: Car,
    priority: 2,
  },
  {
    id: "modules",
    label: "Módulos",
    icon: Settings,
    priority: 3,
  },
  {
    id: "notifications",
    label: "Notificações",
    icon: Bell,
    priority: 4,
  },
];

const TAB_FIELDS_MAPPING = {
  user_data: ["user_data"] as const,
  vehicles: ["vehicles"] as const,
  modules: ["modules"] as const,
} as const;

type StepId = (typeof STEPS)[number]["id"];

type UserResponseData = {
  name: string;
  email: string | null;
  user: string | null;
  status: number;
  enable_notifications?: number | boolean | null;
  access_level_id?: number | null;
};

const UserForm = () => {
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [currentStep, setCurrentStep] = useState<StepId>("user_data");
  const { id } = useParams();
  const navigate = useNavigate();

  // Initialize form
  const form = useForm<UserFormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<UserFormValues>,
    shouldFocusError: true,
    defaultValues: {
      user_data: {
        name: "",
        email: null,
        user: null,
        password: null,
        status: 1,
        enable_notifications: false,
        access_level_id: null,
      },
      vehicles: [],
      modules: [],
    },
  });

  // Hook para navegação de erros entre abas
  const { navigateToFirstTab, tabHasErrors } =
    useTabErrorNavigation<UserFormValues>({
      errors: form.formState.errors,
      tabFieldsMapping: TAB_FIELDS_MAPPING,
      currentTab: currentStep,
      setCurrentTab: setCurrentStep,
      tabs: STEPS,
    });

  const { fields: vehicleFields } = useFieldArray({
    control: form.control,
    name: "vehicles",
  });

  const { fields: moduleFields } = useFieldArray({
    control: form.control,
    name: "modules",
  });

  // Load user data
  useEffect(() => {
    const loadUser = async () => {
      try {
        if (id) {
          const [
            userResponse,
            vehiclePermissionsResponse,
            modulePermissionsResponse,
          ] = await Promise.all([
            userService.show(Number(id)),
            userService.getVehiclePermissions(Number(id)),
            userService.getModulePermissions(Number(id), 1),
          ]);

          const vehicleData = Object.entries(
            vehiclePermissionsResponse.data,
          ).map(([key, value]) => ({
            vehicle_id: Number(key),
            ...value,
          }));

          const moduleData = Object.entries(modulePermissionsResponse.data)
            .map(([key, value]) => ({
              module_id: Number(key),
              ...value,
            }))
            .sort((a, b) => a.module_title.localeCompare(b.module_title));

          const userData = userResponse.data as UserResponseData;

          form.reset({
            user_data: {
              name: userData.name,
              email: userData.email,
              user: userData.user,
              password: null,
              status: userData.status,
              enable_notifications: Boolean(userData.enable_notifications ?? 0),
              access_level_id: userData.access_level_id ?? null,
            },
            vehicles: vehicleData,
            modules: moduleData,
          });
        } else {
          const [responseVehicles, responseModules] = await Promise.all([
            userService.getVehicles(0, { platform_id: 1, summarized: true }),
            userService.getModules(0, 1),
          ]);

          const vehicleData = responseVehicles.data.map((vehicle) => ({
            vehicle_id: vehicle.id,
            permission: 0,
            title: vehicle.title,
            plate: vehicle.plate,
            authorization_to_drive: 0,
            authorization_to_drive_label: "",
          }));

          const moduleData = responseModules.data.map((module) => ({
            module_id: module.id,
            permission: 0,
            module_title: module.name,
            parent_id: module.parent_id,
          }));

          form.reset({
            user_data: {
              name: "",
              email: null,
              user: null,
              password: null,
              status: 1,
              enable_notifications: false,
            },
            vehicles: vehicleData,
            modules: moduleData,
          });
        }
      } catch (err) {
        errorHandler(err, "Erro ao carregar usuário");
        navigate("/users");
      }
    };

    loadUser().finally(() => setShowSkeleton(false));
  }, [id, form.reset, navigate, form]);

  // Use custom hook for form submission
  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: userService.create,
    updateService: userService.update,
    onSuccess: () => navigate("/users"),
  });

  // Form submission handler
  const onSubmit = async (values: UserFormValues) => {
    try {
      await handleSubmit({
        ...values,
        user_data: {
          ...values.user_data,
          type: 0, // Usuário comum
          enable_notifications: values.user_data.enable_notifications ? 1 : 0,
        },
      });
    } catch (err) {
      errorHandler(err, "Erro ao salvar usuário");

      const firstTab = navigateToFirstTab();
      if (firstTab && firstTab !== currentStep) {
        setTimeout(() => setCurrentStep(firstTab), 100);
      }
    }
  };

  const currentStepIndex = STEPS.findIndex((step) => step.id === currentStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === STEPS.length - 1;

  const goToNextStep = () => {
    if (!isLastStep) {
      const nextStep = STEPS[currentStepIndex + 1];
      setCurrentStep(nextStep.id);
    }
  };

  const goToPreviousStep = () => {
    if (!isFirstStep) {
      const previousStep = STEPS[currentStepIndex - 1];
      setCurrentStep(previousStep.id);
    }
  };

  const handleStepChange = (value: string) => {
    setCurrentStep(value as StepId);
  };

  const onError: SubmitErrorHandler<UserFormValues> = () => {
    const firstTab = navigateToFirstTab();
    if (firstTab) {
      setCurrentStep(firstTab);
    }
  };

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Usuário" : "Novo Usuário"}</CardTitle>
      </CardHeader>
      <CardContent>
        {showSkeleton ? (
          <UserFormSkeleton />
        ) : (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit, onError)}
              className="space-y-8"
            >
              <Tabs
                value={currentStep}
                onValueChange={handleStepChange}
                defaultValue="user_data"
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-4">
                  {STEPS.map((step, index) => (
                    <TabsTrigger
                      key={step.id}
                      value={step.id}
                      disabled={step.id === "notifications" && !id}
                      className={cn(
                        "flex items-center space-x-2 relative",
                        index < currentStepIndex && "text-primary",
                        index === currentStepIndex &&
                          "bg-primary text-primary-foreground",
                        tabHasErrors(step.id) &&
                          "ring-2 ring-red-500 ring-offset-2",
                      )}
                    >
                      <step.icon className="w-4 h-4" />
                      <span>{step.label}</span>

                      {/* Indicador visual de erro */}
                      {tabHasErrors(step.id) && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* User Data Tab */}
                <UserDataTab form={form} />

                {/* Vehicles Tab */}
                <UserVehiclesTab fields={vehicleFields} form={form} />

                {/* Modules Tab */}
                <UserModulesTab fields={moduleFields} form={form} />

                {/* Notifications Tab */}
                <UserNotificationsTab userId={id ? Number(id) : null} />
              </Tabs>

              <div className="flex justify-between pt-8 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToPreviousStep}
                  disabled={isFirstStep}
                  className="flex items-center space-x-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Anterior</span>
                </Button>

                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => navigate("/users")}
                  >
                    Cancelar
                  </Button>

                  {!isLastStep && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={goToNextStep}
                      className="flex items-center space-x-2"
                    >
                      <span>Próximo</span>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  )}

                  <SubmitButton loading={loading} />
                </div>
              </div>
            </form>
          </Form>
        )}
      </CardContent>
    </Card>
  );
};

export default UserForm;
